import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { useUser } from '../contexts/UserContext'
import { userService } from '../services/userService'

interface CompanyLogoUploadProps {
  currentLogoUrl?: string | null
  onLogoUpdate?: (logoUrl: string | null) => void
  size?: 'small' | 'medium' | 'large'
  showRemoveButton?: boolean
  iconOnly?: boolean
}

const CompanyLogoUpload: React.FC<CompanyLogoUploadProps> = ({
  currentLogoUrl,
  onLogoUpdate,
  size = 'medium',
  showRemoveButton = true,
  iconOnly = false,
}) => {
  const { user, refreshUser } = useUser()
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [isRemoved, setIsRemoved] = useState<boolean>(false)
  const [previousLogoUrl, setPreviousLogoUrl] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)

  // Check if user can manage company logos
  const canManageCompanyLogo =
    user &&
    ['OWNER', 'PROJECT_MANAGER', 'ADMINISTRATOR'].includes(user.user_type)

  // File validation function
  const validateFile = useCallback((file: File) => {
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      setLogoError('Only PNG, JPEG, or GIF files are allowed.')
      return false
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('File size must be under 5MB.')
      return false
    }

    setLogoError(null)
    return true
  }, [])

  // Handle file drop/selection
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file && validateFile(file)) {
        // Revoke previous preview URL to prevent memory leak
        if (logoPreview) {
          URL.revokeObjectURL(logoPreview)
        }

        setLogoFile(file)
        setLogoPreview(URL.createObjectURL(file))
        setIsRemoved(false) // Reset removed state when uploading new logo
      }
    },
    [logoPreview, validateFile],
  )

  // Configure react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
    },
    maxFiles: 1,
    disabled: !canManageCompanyLogo || isUploadingLogo || !!logoPreview,
  })

  // Cleanup blob URL when component unmounts or logoPreview changes
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
      }
    }
  }, [logoPreview])

  // Reset removed state when currentLogoUrl changes (user context refresh)
  // Only reset if we get a genuinely new logo URL (different from previous)
  useEffect(() => {
    if (
      currentLogoUrl !== undefined &&
      currentLogoUrl !== null &&
      currentLogoUrl !== previousLogoUrl
    ) {
      setIsRemoved(false)
      setPreviousLogoUrl(currentLogoUrl)
    }
  }, [currentLogoUrl, previousLogoUrl])

  const sizeStyles = {
    small: { width: 100, height: 50 },
    medium: { width: 200, height: 100 },
    large: { width: 300, height: 150 },
  }

  const handleLogoUpload = async () => {
    if (!logoFile) return

    if (!canManageCompanyLogo) {
      toast.error('You do not have permission to upload company logos.')
      return
    }

    setIsUploadingLogo(true)
    setLogoError(null)

    try {
      const updatedUser = await userService.uploadCompanyLogo(logoFile)
      const newLogoUrl = updatedUser.logo_url || null

      // Update user context
      await refreshUser()

      // Call the callback if provided
      onLogoUpdate?.(newLogoUrl)

      // Revoke the preview URL before clearing state
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
      }

      setLogoFile(null)
      setLogoPreview(null)
      toast.success('Company logo uploaded successfully', {
        style: { color: '#31C48D' },
      })
    } catch (error) {
      setLogoError('Failed to upload company logo. Please try again.')
      console.error('Error uploading company logo:', error)
      toast.error('Failed to upload company logo. Please try again.')
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const handleRemoveLogo = () => {
    console.log('Remove logo clicked')
    console.log('Current displayLogoUrl:', displayLogoUrl)
    console.log('Current currentLogoUrl:', currentLogoUrl)
    console.log('Current user?.logo_url:', user?.logo_url)

    // Revoke the preview URL if it exists
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview)
    }

    console.log('Calling onLogoUpdate with null')
    onLogoUpdate?.(null)
    setLogoFile(null)
    setLogoPreview(null)
    setIsRemoved(true)
    setPreviousLogoUrl(null) // Clear previous logo URL when removed

    console.log('After removal - displayLogoUrl should be null')
  }

  // Show currentLogoUrl if explicitly set, otherwise show user's logo
  // If logo was removed, don't show any logo
  const displayLogoUrl = isRemoved
    ? null
    : currentLogoUrl !== undefined
      ? currentLogoUrl
      : user?.logo_url

  return (
    <div className={css(styles.logoSection)} style={sizeStyles[size]}>
      {displayLogoUrl && !logoPreview ? (
        <div className={css(styles.logoContainer)}>
          <img
            src={displayLogoUrl}
            alt="Company Logo"
            className={css(styles.logoImage)}
          />
          {showRemoveButton && canManageCompanyLogo && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveLogo()
              }}
              className={css(styles.removeLogoButton)}
              title="Remove logo"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={css(
            styles.logoPlaceholder,
            styles.logoUploadArea,
            isDragActive && styles.dragActive,
          )}
          style={{
            position: 'relative',
            padding: 0,
            width: '100%',
            height: '100%',
            cursor:
              canManageCompanyLogo && !isUploadingLogo && !logoPreview
                ? 'pointer'
                : 'default',
          }}
        >
          <input {...getInputProps()} />
          {!logoPreview ? (
            <div style={{ textAlign: 'center' }}>
              <i
                className="fa-regular fa-image"
                style={{
                  fontSize: size === 'small' ? 16 : size === 'medium' ? 32 : 48,
                  color: '#9CA3AF',
                  display: 'block',
                  marginBottom: iconOnly ? 0 : size === 'small' ? 4 : 8,
                }}
              />
              {!iconOnly &&
                (canManageCompanyLogo ? (
                  <span
                    style={{
                      fontSize: size === 'small' ? 10 : 12,
                      color: '#6B7280',
                      fontWeight: 500,
                    }}
                  >
                    {isDragActive
                      ? 'Drop the file here...'
                      : 'Click to upload or drag and drop'}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: size === 'small' ? 10 : 12,
                      color: '#9CA3AF',
                      fontWeight: 500,
                    }}
                  >
                    No logo uploaded
                  </span>
                ))}
            </div>
          ) : (
            <img
              src={logoPreview}
              alt="Preview"
              className={css(styles.previewImage)}
            />
          )}
          {logoError && (
            <div className={css(styles.errorMessage)}>{logoError}</div>
          )}
        </div>
      )}

      {logoPreview && canManageCompanyLogo && (
        <div className={css(styles.uploadActions)}>
          <button
            type="button"
            onClick={handleLogoUpload}
            disabled={isUploadingLogo}
            className={css(styles.uploadButton)}
          >
            {isUploadingLogo ? 'Uploading...' : 'Upload'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              // Revoke the preview URL before clearing state
              if (logoPreview) {
                URL.revokeObjectURL(logoPreview)
              }
              setLogoFile(null)
              setLogoPreview(null)
            }}
            disabled={isUploadingLogo}
            className={css(styles.cancelButton)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  logoSection: {
    position: 'relative',
  },
  logoContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 6,
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    border: '2px dashed #D1D5DB',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    position: 'relative',
    padding: 0,
  },
  logoUploadArea: {
    transition: 'border-color 0.2s',
    ':hover': {
      borderColor: '#1C64F2',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
    },
  },
  dragActive: {
    borderColor: '#1C64F2',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderStyle: 'solid',
  },
  previewImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 6,
    margin: 0,
    padding: 0,
    pointerEvents: 'none',
  },
  removeLogoButton: {
    position: 'absolute',
    top: -8,
    right: 4,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    fontSize: '12px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#374151',
      transform: 'scale(1.05)',
    },
  },
  uploadActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginTop: 8,
  },
  uploadButton: {
    background: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '1px 10px',
    cursor: 'pointer',
    fontSize: 11,
    height: 22,
    minWidth: 44,
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  cancelButton: {
    background: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: 4,
    padding: '1px 10px',
    cursor: 'pointer',
    fontSize: 11,
    height: 22,
    minWidth: 44,
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  errorMessage: {
    color: 'red',
    fontSize: 12,
    marginTop: 4,
    position: 'absolute',
    bottom: -20,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
})

export default CompanyLogoUpload
